import React, { Suspense, lazy } from "react";
import { Routes, Route } from "react-router-dom";
import Loader from "../Loader";
import ProtectedRoute from "./ProtectedRoute";
import { SUPER_ADMIN_ONLY } from "../../utils/auth";

// Every page is lazy-loaded straight from its module (not the pages barrel —
// importing the barrel would pull all ~80 pages back into one chunk). Heavy
// dependencies (Datatable, chart.js, quill, Firebase chat) load on first
// navigation instead of at login.
const Ecommerce = lazy(() => import("../../pages/Ecommerce"));
const Customers = lazy(() => import("../../pages/Customers/src/components/List"));
const CustomerAdd = lazy(() => import("../../pages/Customers/src/components/Add"));
const CustomerDetail = lazy(() => import("../../pages/Customers/src/components/Detail"));
const Roles = lazy(() => import("../../pages/Roles/src/components/List"));
const RoleAdd = lazy(() => import("../../pages/Roles/src/components/Add"));
const RoleDetail = lazy(() => import("../../pages/Roles/src/components/Detail"));
const Company = lazy(() => import("../../pages/Company/src/components/List"));
const CompanyAdd = lazy(() => import("../../pages/Company/src/components/Add"));
const CompanyDetail = lazy(() => import("../../pages/Company/src/components/Detail"));
const Skills = lazy(() => import("../../pages/Skills/src/components/List_Old"));
const SubSkills = lazy(() => import("../../pages/Skills/src/components/SubSkills"));
const MainSkills = lazy(() => import("../../pages/Skills/src/components/MainCategories"));
const MainCategoryAdd = lazy(() => import("../../pages/Skills/src/components/MainCategoryAdd"));
const SkillAdd = lazy(() => import("../../pages/Skills/src/components/Add"));
const SkillDetail = lazy(() => import("../../pages/Skills/src/components/Detail"));
const Settings = lazy(() => import("../../pages/Settings/src/components/Settings"));
const ActivityLog = lazy(() => import("../../pages/Settings/src/components/ActivityLog/ActivityLog"));
const Jobs = lazy(() => import("../../pages/Jobs/src/components/List"));
const JobAdd = lazy(() => import("../../pages/Jobs/src/components/Add"));
const JobDetail = lazy(() => import("../../pages/Jobs/src/components/Detail"));
const Geeks = lazy(() => import("../../pages/Geeks/src/components/List"));
const GeekAdd = lazy(() => import("../../pages/Geeks/src/components/Add"));
const GeekDetail = lazy(() => import("../../pages/Geeks/src/components/Detail"));
const GeekSkills = lazy(() => import("../../pages/Geeks/src/components/GeekSkills"));
const GeekReviews = lazy(() => import("../../pages/Geeks/src/components/GeekReviews"));
const GeekReviewProfile = lazy(() => import("../../pages/Geeks/src/components/GeekReviewProfile"));
const Wallets = lazy(() => import("../../pages/Wallets/src/components/List"));
const WalletAdd = lazy(() => import("../../pages/Wallets/src/components/Add"));
const WalletDetailScreen = lazy(() => import("../../pages/Wallets/src/components/Detail"));
const WalletTransactions = lazy(() => import("../../pages/Wallets/src/components/WalletTransactions"));
const AllTransactions = lazy(() => import("../../pages/WalletTransactions/src/components/List"));
const WalletTransactionDetail = lazy(() => import("../../pages/WalletTransactions/src/components/Detail"));
const WithdrawalRequests = lazy(() => import("../../pages/WithdrawalRequests/src/components/List"));
const WithdrawalRequestAdd = lazy(() => import("../../pages/WithdrawalRequests/src/components/Add"));
const WithdrawalRequestDetail = lazy(() => import("../../pages/WithdrawalRequests/src/components/Detail"));
const WithdrawAuditLog = lazy(() => import("../../pages/WalletAuditLog/src/components/List"));
const WalletAuditLogDetail = lazy(() => import("../../pages/WalletAuditLog/src/components/Detail"));
const Notifications = lazy(() => import("../../pages/Notifications/src/components/List"));
const NotificationAdd = lazy(() => import("../../pages/Notifications/src/components/Add"));
const NotificationDetail = lazy(() => import("../../pages/Notifications/src/components/Detail"));
const Users = lazy(() => import("../../pages/Users/src/components/List"));
const UserAdd = lazy(() => import("../../pages/Users/src/components/Add"));
const UserDetail = lazy(() => import("../../pages/Users/src/components/Detail"));
const Disputes = lazy(() => import("../../pages/Disputes/src/components/List"));
const DisputesDetail = lazy(() => import("../../pages/Disputes/src/components/Detail"));
const NoShowCases = lazy(() => import("../../pages/NoShowCases/src/components/List"));
const NoShowCaseDetail = lazy(() => import("../../pages/NoShowCases/src/components/Detail"));
const ReportIssue = lazy(() => import("../../pages/ReportIssue/src/components/List"));
const PauseJob = lazy(() => import("../../pages/PauseJob/src/components/List"));
const FaqCategories = lazy(() => import("../../pages/FaqCategories/src/components/List"));
const FaqCategoriesCreate = lazy(() => import("../../pages/FaqCategories/src/components/Add"));
const FaqCategoriesDetail = lazy(() => import("../../pages/FaqCategories/src/components/Detail"));
const FaqCategoriesEdit = lazy(() => import("../../pages/FaqCategories/src/components/Edit"));
const Faqs = lazy(() => import("../../pages/Faqs/src/components/List"));
const FaqsCreate = lazy(() => import("../../pages/Faqs/src/components/Add"));
const FaqsDetail = lazy(() => import("../../pages/Faqs/src/components/Detail"));
const FaqsEdit = lazy(() => import("../../pages/Faqs/src/components/Edit"));
const PrivacyPolicy = lazy(() => import("../../pages/PrivacyPolicy/src/components/List"));
const PrivacyPolicyCreate = lazy(() => import("../../pages/PrivacyPolicy/src/components/Add"));
const PrivacyPolicyDetail = lazy(() => import("../../pages/PrivacyPolicy/src/components/Detail"));
const PrivacyPolicyEdit = lazy(() => import("../../pages/PrivacyPolicy/src/components/Edit"));
const TermsAndConditions = lazy(() => import("../../pages/TermsAndConditions/src/components/List"));
const TermsAndConditionsCreate = lazy(() => import("../../pages/TermsAndConditions/src/components/Add"));
const TermsAndConditionsDetail = lazy(() => import("../../pages/TermsAndConditions/src/components/Detail"));
const TermsAndConditionsEdit = lazy(() => import("../../pages/TermsAndConditions/src/components/Edit"));
const Fines = lazy(() => import("../../pages/Fines/src/components/List"));
const FinesCreate = lazy(() => import("../../pages/Fines/src/components/Add"));
const FinesDetail = lazy(() => import("../../pages/Fines/src/components/Detail"));
const ReferFriendList = lazy(() => import("../../pages/ReferFriend/src/components/List"));
const ReferDetail = lazy(() => import("../../pages/ReferFriend/src/components/Detail"));
const SecondaryWallet = lazy(() => import("../../pages/SecondaryWallet/src/components/List"));
const SecondaryWalletDetail = lazy(() => import("../../pages/SecondaryWallet/src/components/Detail"));
const EmployeeStatementCycle = lazy(() => import("../../pages/SecondaryWallet/src/components/EmployeeStatement/List"));
const EmployeeStatementCycleDetail = lazy(() => import("../../pages/SecondaryWallet/src/components/EmployeeStatement/Detail"));
const KycAccountConnect = lazy(() => import("../../pages/KycAccountConnect/src/components/List"));
const ChatApp = lazy(() => import("../../pages/Chat/ChatApp"));
const ChatReports = lazy(() => import("../../pages/ChatReports/src/components/List"));

const AppRoutes = () => {
  // Wrap a money/auth-sensitive screen so only Super Admin can reach it.
  // Non-super-admins get a 403 instead of the screen (MOH-394 frontend split).
  const sa = (element) => (
    <ProtectedRoute requiredRoles={SUPER_ADMIN_ONLY}>{element}</ProtectedRoute>
  );

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route path="/" element={<Ecommerce />} />
        <Route path="/dashboard" element={<Ecommerce />} />
        {/* pages  */}
        <Route path="/settings" element={sa(<Settings />)} />
        <Route path="/activity-log" element={<ActivityLog />} />
        <Route path="/disputes" element={sa(<Disputes />)} />
        <Route path="/disputes/:id/detail" element={sa(<DisputesDetail />)} />
        {/* No-show cases move money (refunds / fines / freeze release) — Super
            Admin only, same split as Disputes (MOH-394). */}
        <Route path="/no-show-cases" element={sa(<NoShowCases />)} />
        <Route path="/no-show-cases/:id/detail" element={sa(<NoShowCaseDetail />)} />
        <Route path="/reportissues" element={<ReportIssue />} />
        <Route path="/pausejob" element={<PauseJob />} />
        <Route path="/chat" element={<ChatApp />} />
        <Route path="/chat-reports" element={<ChatReports />} />
        <Route path="/fines" element={sa(<Fines />)} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/customer/add" element={<CustomerAdd />} />
        <Route path="/customers/:id/edit" element={<CustomerAdd />} />
        <Route path="/customers/:id/detail" element={<CustomerDetail />} />
        <Route path="/roles" element={sa(<Roles />)} />
        <Route path="/role/add" element={sa(<RoleAdd />)} />
        <Route path="/roles/:id/detail" element={sa(<RoleDetail />)} />
        <Route path="/categories" element={<Skills />} />
        <Route path="/main-categories" element={<MainSkills />} />
        <Route path="/sub-categories/:id" element={<SubSkills />} />
        <Route path="/categories/add" element={<SkillAdd />} />
        <Route path="/main-categories/add" element={<MainCategoryAdd />} />
        <Route path="/categories/:id/edit" element={<SkillAdd />} />
        <Route path="/categories/:id/detail" element={<SkillDetail />} />
        {/* Users */}
        <Route path="/users" element={<Users />} />
        <Route path="/user/add" element={<UserAdd />} />
        <Route path="/user/:id/detail" element={<UserDetail />} />
        {/* Notifications */}
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/notification/add" element={<NotificationAdd />} />
        <Route path="/notification/:id/detail" element={<NotificationDetail />} />
        {/* WithdrawalRequests */}
        <Route path="/withdrawal-requests" element={sa(<WithdrawalRequests />)} />
        <Route path="/wallet-audit-log" element={sa(<WithdrawAuditLog />)} />
        <Route
          path="/wallet-audit-logs/:id/detail"
          element={sa(<WalletAuditLogDetail />)}
        />
        <Route
          path="/withdrawal-request/add"
          element={sa(<WithdrawalRequestAdd />)}
        />
        <Route
          path="/withdrawal-request/:id/detail"
          element={sa(<WithdrawalRequestDetail />)}
        />
        {/* Wallet Transactions */}
        <Route path="/transactions" element={sa(<AllTransactions />)} />
        <Route path="/transactions/:id" element={sa(<WalletTransactionDetail />)} />
        {/* Wallets */}
        <Route path="/wallets" element={sa(<Wallets />)} />
        <Route path="/wallet/add" element={sa(<WalletAdd />)} />
        <Route path="/wallet/:id" element={sa(<WalletDetailScreen />)} />
        <Route path="/wallet-transactions/:id" element={sa(<WalletTransactions />)} />
        {/* Secondary Wallet */}
        <Route path="/secondary-wallets" element={sa(<SecondaryWallet />)} />
        <Route
          path="/secondary-wallets/:id"
          element={sa(<SecondaryWalletDetail />)}
        />
        {/* employe Statement Cycle */}
        <Route path="/statement-cycles" element={sa(<EmployeeStatementCycle />)} />
        <Route path="/statement-cycle/:id" element={sa(<EmployeeStatementCycleDetail />)} />
        {/* KYC Account Connect */}
        <Route path="/kyc-account-connect" element={sa(<KycAccountConnect />)} />

        {/* Jobs */}
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/job/add" element={<JobAdd />} />
        <Route path="/jobs/:id/detail" element={<JobDetail />} />
        {/* help menu */}
        <Route path="/faqcategories" element={<FaqCategories />} />
        <Route path="/faqcategories/add" element={<FaqCategoriesCreate />} />
        <Route
          path="/faqcategories/:id/detail"
          element={<FaqCategoriesDetail />}
        />
        <Route path="/faqcategories/:id/edit" element={<FaqCategoriesEdit />} />
        {/* help menu Faqs*/}
        <Route path="/faqs" element={<Faqs />} />
        <Route path="/faqs/add" element={<FaqsCreate />} />
        <Route path="/faqs/:id/detail" element={<FaqsDetail />} />
        <Route path="/faqs/:id/edit" element={<FaqsEdit />} />
        {/* help menu Privacy Policy*/}
        <Route path="/privacypolicy" element={<PrivacyPolicy />} />
        <Route path="/privacypolicy/add" element={<PrivacyPolicyCreate />} />
        <Route
          path="/privacypolicy/:id/detail"
          element={<PrivacyPolicyDetail />}
        />
        <Route path="/privacypolicy/:id/edit" element={<PrivacyPolicyEdit />} />
        {/* help menu Terms And Conditions*/}
        <Route path="/termsandconditions" element={<TermsAndConditions />} />
        <Route
          path="/termsandconditions/add"
          element={<TermsAndConditionsCreate />}
        />
        <Route
          path="/termsandconditions/:id/detail"
          element={<TermsAndConditionsDetail />}
        />
        <Route
          path="/termsandconditions/:id/edit"
          element={<TermsAndConditionsEdit />}
        />
        {/* {/* Fines/} */}
        <Route path="/fines/add" element={sa(<FinesCreate />)} />
        <Route path="/fines/:id/edit" element={sa(<FinesCreate />)} />
        <Route path="/fines/:id/detail" element={sa(<FinesDetail />)} />
        {/* Geeks */}
        <Route path="/geeks" element={<Geeks />} />
        <Route path="/geek/add" element={<GeekAdd />} />
        <Route path="/geeks/:id/edit" element={<GeekAdd />} />
        <Route path="/geeks/:id/detail" element={<GeekDetail />} />
        <Route path="/geeks/:id/review" element={<GeekReviewProfile />} />
        <Route path="/geek-skills/:id" element={<GeekSkills />} />
        <Route path="/geek-reviews/:id" element={<GeekReviews />} />
        {/* Refer Friend */}
        <Route path="/refer-friend-list" element={<ReferFriendList />} />
        <Route path="/admin/referrals/:id/detail" element={<ReferDetail />} />
        {/* Company */}
        <Route path="/companies" element={<Company />} />
        <Route path="/company/add" element={<CompanyAdd />} />
        <Route path="/companies/:id/edit" element={<CompanyAdd />} />
        <Route path="/companies/:id/detail" element={<CompanyDetail />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
